/**
 * Per-instance mouse repulsion, as an offset from wherever the thing would
 * otherwise be.
 *
 * The offset is deliberately kept separate from the base position rather than
 * being written into it. The cluster's positions belong to GSAP and the orbit
 * field derives its own from an exact circle, so anything that wrote to those
 * would either be stomped by the next tween tick or would break the guarantee
 * that an orbiting body can't drift through the middle. Adding an offset at
 * matrix-build time leaves both untouched, and means "resume the auto
 * animation" needs no state machine: the offset springs back to zero and the
 * underlying loop is exactly where it always was.
 *
 * Each instance is a damped spring being shoved by the cursor:
 *
 *     accel = push(cursor) - stiffness * offset - damping * velocity
 *
 * with damping set critical (2 * sqrt(stiffness)) by default, so things return
 * home without oscillating around it.
 */
export default class Repulsor {
    constructor(count, options) {
        const {
            // Peak push acceleration, units/s^2, at the centre of the cone.
            strength = 20,
            // Cone half-angle as a tangent. The influence radius grows with
            // distance from the camera, so it covers a constant-ish disc on
            // screen instead of a bubble that only works at one depth.
            angle = 0.16,
            // Floor on that radius, for anything very close to the camera.
            minRadius = 1,
            stiffness = 3.5,
            damping = 2 * Math.sqrt(stiffness),
            // Cap, so nothing can be flung out of the scene.
            maxOffset = 6
        } = options;

        this.strength = strength;
        this.angle = angle;
        this.minRadius = minRadius;
        this.stiffness = stiffness;
        this.damping = damping;
        this.maxOffset = maxOffset;

        this.offsets = new Float32Array(count * 3);
        this.velocities = new Float32Array(count * 3);

        // A fixed random direction per instance, used when an instance sits
        // almost exactly on the cursor ray and the "away from the cursor"
        // direction is degenerate. This is what scatters the central cluster
        // when the tween has drawn it all into the middle and the cursor is
        // sitting right on top of it: with nowhere in particular to go, every
        // instance takes its own direction instead.
        this.bursts = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            const z = Math.random() * 2 - 1;
            const a = Math.random() * Math.PI * 2;
            const r = Math.sqrt(1 - z * z);
            this.bursts[i * 3] = r * Math.cos(a);
            this.bursts[i * 3 + 1] = r * Math.sin(a);
            this.bursts[i * 3 + 2] = z;
        }

        // Largest |offset| + |velocity| seen last frame, so a field that has
        // settled and has no cursor near it can skip the whole loop.
        this.energy = 0;
    }

    get idle() {
        return this.energy < 1e-4;
    }

    /**
     * Drop the push entirely. Used when a field is hidden: its update loop
     * stops, which would otherwise freeze the offsets mid-shove and snap
     * everything back into place when the field is shown again.
     */
    reset() {
        if (this.idle) return;
        this.offsets.fill(0);
        this.velocities.fill(0);
        this.energy = 0;
    }

    /** Central cluster / orbit field: base positions live on Object3D proxies. */
    stepDummies(delta, pointer, dummies) {
        if (!pointer.active && this.idle) return;
        this.energy = 0;
        for (let i = 0; i < dummies.length; i++) {
            const p = dummies[i].position;
            this.integrate(i, p.x, p.y, p.z, delta, pointer);
        }
    }

    /** Particles: base positions are a flat xyz buffer. */
    stepBuffer(delta, pointer, base, count) {
        if (!pointer.active && this.idle) return;
        this.energy = 0;
        for (let i = 0; i < count; i++) {
            const o = i * 3;
            this.integrate(i, base[o], base[o + 1], base[o + 2], delta, pointer);
        }
    }

    integrate(i, bx, by, bz, delta, pointer) {
        const o = i * 3;
        const offsets = this.offsets;
        const vel = this.velocities;

        let ax = 0;
        let ay = 0;
        let az = 0;

        if (pointer.active) {
            // Where the thing actually is, offset included - so a shape already
            // pushed aside stops being pushed once it's clear of the cursor.
            const px = bx + offsets[o] - pointer.origin.x;
            const py = by + offsets[o + 1] - pointer.origin.y;
            const pz = bz + offsets[o + 2] - pointer.origin.z;

            const dx = pointer.direction.x;
            const dy = pointer.direction.y;
            const dz = pointer.direction.z;

            // Distance along the ray. Negative means it's behind the camera.
            const t = px * dx + py * dy + pz * dz;
            if (t > 0) {
                const perpX = px - dx * t;
                const perpY = py - dy * t;
                const perpZ = pz - dz * t;
                const dist = Math.sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);
                const radius = Math.max(this.minRadius, t * this.angle);

                if (dist < radius) {
                    // Linear falloff: full push under the cursor, nothing at
                    // the rim, so nothing pops as it crosses out of the cone.
                    //
                    // The push is measured from where the thing currently is,
                    // offset included, so it's a real field - a shape shoved
                    // clear of the cursor stops being shoved. That makes the
                    // radius, not the cap, what actually sets how far things
                    // travel: they come to rest where the push balances the
                    // spring, at strength * radius / (stiffness * radius +
                    // strength). Worth remembering when tuning - raising
                    // strength alone runs into the rim.
                    const force = this.strength * (1 - dist / radius);

                    // Blend the outward direction into the instance's own
                    // burst direction as it approaches the ray, where "outward"
                    // stops meaning anything.
                    const w = Math.min(1, dist / (radius * 0.35));
                    const inv = dist > 1e-6 ? 1 / dist : 0;
                    let nx = this.bursts[o] * (1 - w) + perpX * inv * w;
                    let ny = this.bursts[o + 1] * (1 - w) + perpY * inv * w;
                    let nz = this.bursts[o + 2] * (1 - w) + perpZ * inv * w;

                    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
                    if (len > 1e-6) {
                        ax = (nx / len) * force;
                        ay = (ny / len) * force;
                        az = (nz / len) * force;
                    }
                }
            }
        }

        // Spring home.
        const k = this.stiffness;
        const c = this.damping;
        let vx = vel[o] + (ax - k * offsets[o] - c * vel[o]) * delta;
        let vy = vel[o + 1] + (ay - k * offsets[o + 1] - c * vel[o + 1]) * delta;
        let vz = vel[o + 2] + (az - k * offsets[o + 2] - c * vel[o + 2]) * delta;

        let ox = offsets[o] + vx * delta;
        let oy = offsets[o + 1] + vy * delta;
        let oz = offsets[o + 2] + vz * delta;

        const mag = Math.sqrt(ox * ox + oy * oy + oz * oz);
        if (mag > this.maxOffset) {
            const s = this.maxOffset / mag;
            ox *= s;
            oy *= s;
            oz *= s;
            // Kill the outward velocity too, or it keeps pressing on the cap.
            vx *= s;
            vy *= s;
            vz *= s;
        }

        offsets[o] = ox;
        offsets[o + 1] = oy;
        offsets[o + 2] = oz;
        vel[o] = vx;
        vel[o + 1] = vy;
        vel[o + 2] = vz;

        const e = mag + Math.sqrt(vx * vx + vy * vy + vz * vz);
        if (e > this.energy) this.energy = e;
    }
}
